import { useState, useRef } from 'react';
import { Modal, Form, Input, Radio, Button, Space, Image, Typography, App, Tag } from 'antd';
import { BugOutlined, BulbOutlined, CameraOutlined, DeleteOutlined, UserOutlined, TeamOutlined, UploadOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import feedbackServiceInstance, { type FeedbackType } from '../../api/feedbackService';
import { useTheme } from '../../contexts/useTheme';
import { useAuth } from '../../hooks/useAuth';

const { Text } = Typography;

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

export const FeedbackModal = ({ open, onClose }: FeedbackModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [screenshotMode, setScreenshotMode] = useState<'capture' | 'upload'>('capture');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { theme } = useTheme();
  const { message } = App.useApp();
  const { authState } = useAuth();

  const handleCaptureScreenshot = async () => {
    setCapturingScreenshot(true);

    // Get the modal element to hide it temporarily
    const modalWrapper = document.querySelector('.ant-modal-wrap') as HTMLElement;
    const modalMask = document.querySelector('.ant-modal-mask') as HTMLElement;

    // Hide modal and mask
    if (modalWrapper) modalWrapper.style.visibility = 'hidden';
    if (modalMask) modalMask.style.visibility = 'hidden';

    try {
      // Small delay to let the modal disappear visually
      await new Promise(resolve => setTimeout(resolve, 150));

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: 0.75, // Reduce size for faster upload
        ignoreElements: (element) => {
          // Ignore any modal elements that might still be in DOM
          return element.classList.contains('ant-modal-root');
        },
        onclone: (clonedDoc) => {
          // Handle WebGL canvases (like Mapbox GL maps) that need special treatment
          // Find all canvas elements in the original document and copy their content to cloned ones
          const originalCanvases = document.querySelectorAll('canvas');
          const clonedCanvases = clonedDoc.querySelectorAll('canvas');
          
          originalCanvases.forEach((originalCanvas, index) => {
            const clonedCanvas = clonedCanvases[index];
            if (clonedCanvas && originalCanvas.width > 0 && originalCanvas.height > 0) {
              try {
                // Get the context and draw the original canvas content
                const clonedCtx = clonedCanvas.getContext('2d');
                if (clonedCtx) {
                  // Set canvas dimensions to match original
                  clonedCanvas.width = originalCanvas.width;
                  clonedCanvas.height = originalCanvas.height;
                  // Draw the original canvas onto the cloned one
                  clonedCtx.drawImage(originalCanvas, 0, 0);
                }
              } catch {
                // Canvas might be tainted or have other issues, silently ignore
                console.warn('Could not copy canvas content for screenshot');
              }
            }
          });
        },
      });

      const dataUrl = canvas.toDataURL('image/png', 0.8);
      setScreenshot(dataUrl);
      message.success('Screenshot captured!');
    } catch (error) {
      console.error('Screenshot capture failed:', error);
      message.error('Failed to capture screenshot');
    } finally {
      // Show the modal again
      if (modalWrapper) modalWrapper.style.visibility = '';
      if (modalMask) modalMask.style.visibility = '';
      setCapturingScreenshot(false);
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      message.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      message.error('Image size must be less than 10MB');
      return;
    }

    // Read file as data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setScreenshot(dataUrl);
      message.success('Image uploaded successfully!');
    };
    reader.onerror = () => {
      message.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const result = await feedbackServiceInstance.createFeedback({
        type: values.type as FeedbackType,
        title: values.title,
        description: values.description,
        screenshot: screenshot || undefined,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      });

      if (result.success) {
        message.success(
          values.type === 'bug'
            ? 'Bug report submitted successfully! Thank you for helping us improve.'
            : 'Feature request submitted successfully! We appreciate your feedback.'
        );
        form.resetFields();
        setScreenshot(null);
        setScreenshotMode('capture');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onClose();
      } else {
        message.error(result.errorData?.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Submit error:', error);
      message.error('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setScreenshot(null);
    setScreenshotMode('capture');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Get team display name from cached user info (no API call needed)
  const teamDisplayName = authState.user?.teamDisplayName || authState.user?.team || null;

  return (
    <Modal
      title={
        <Space>
          <span style={{ fontSize: 18 }}>Send Feedback</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Submit Feedback
        </Button>,
      ]}
      width={520}
      destroyOnHidden
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          paddingRight: 4,
        },
      }}
    >
      {/* User and Team Info */}
      {authState.user && (
        <div style={{
          marginBottom: 12,
          padding: 8,
          background: theme === 'dark' ? '#1f1f1f' : '#f5f5f5',
          borderRadius: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined style={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)' }} />
            <Text type="secondary" style={{ fontSize: 13 }}>
              <Text strong>{authState.user.name || `${authState.user.firstName} ${authState.user.lastName}`}</Text>
              {' '}({authState.user.email})
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TeamOutlined style={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)' }} />
            <Text type="secondary" style={{ fontSize: 13 }}>
              Team: {teamDisplayName ? (
                <Tag color="blue" style={{ margin: 0 }}>{teamDisplayName}</Tag>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>Not assigned</Text>
              )}
            </Text>
          </div>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{ type: 'bug' }}
        style={{ marginTop: 0 }}
      >
        <Form.Item
          name="type"
          label="What would you like to share?"
        >
          <Radio.Group buttonStyle="solid" style={{ width: '100%' }}>
            <Radio.Button
              value="bug"
              style={{
                width: '50%',
                textAlign: 'center',
              }}
            >
              <BugOutlined style={{ marginRight: 8 }} />
              Bug Report
            </Radio.Button>
            <Radio.Button
              value="feature"
              style={{
                width: '50%',
                textAlign: 'center',
              }}
            >
              <BulbOutlined style={{ marginRight: 8 }} />
              Feature Request
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="title"
          label="Title"
          rules={[
            { required: true, message: 'Please enter a title' },
            { max: 200, message: 'Title must be less than 200 characters' },
          ]}
        >
          <Input
            placeholder="Brief summary of the issue or request"
            maxLength={200}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { required: true, message: 'Please provide details' },
            { max: 2000, message: 'Description must be less than 2000 characters' },
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Please describe the bug or feature in detail. For bugs, include steps to reproduce if possible."
            maxLength={2000}
            showCount
          />
        </Form.Item>

        <Form.Item label="Screenshot (Optional)">
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Radio.Group
              value={screenshotMode}
              onChange={(e) => {
                setScreenshotMode(e.target.value);
                if (screenshot) {
                  handleRemoveScreenshot();
                }
              }}
              buttonStyle="solid"
              style={{ width: '100%' }}
            >
              <Radio.Button
                value="capture"
                style={{
                  width: '50%',
                  textAlign: 'center',
                }}
              >
                <CameraOutlined style={{ marginRight: 8 }} />
                Capture
              </Radio.Button>
              <Radio.Button
                value="upload"
                style={{
                  width: '50%',
                  textAlign: 'center',
                }}
              >
                <UploadOutlined style={{ marginRight: 8 }} />
                Upload
              </Radio.Button>
            </Radio.Group>

            {screenshotMode === 'capture' ? (
              <Button
                icon={<CameraOutlined />}
                onClick={handleCaptureScreenshot}
                loading={capturingScreenshot}
                disabled={!!screenshot}
                block
              >
                {screenshot ? 'Screenshot Captured' : 'Capture Current Screen'}
              </Button>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  icon={<UploadOutlined />}
                  onClick={handleUploadClick}
                  disabled={!!screenshot}
                  block
                >
                  {screenshot ? 'Image Uploaded' : 'Choose Image File'}
                </Button>
              </>
            )}

            {screenshot && (
              <div
                style={{
                  marginTop: 8,
                  padding: 6,
                  border: `1px solid ${theme === 'dark' ? '#434343' : '#d9d9d9'}`,
                  borderRadius: 6,
                  background: theme === 'dark' ? '#1f1f1f' : '#fafafa',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Screenshot preview (click to enlarge)
                  </Text>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveScreenshot}
                  >
                    Remove
                  </Button>
                </div>
                <Image
                  src={screenshot}
                  alt="Screenshot preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 100,
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  preview={{
                    mask: 'Click to view full size',
                  }}
                />
              </div>
            )}
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

